// Dates, numbers and labels in UK English.

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" });
const timeFmt = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });

function parse(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const fmtDate = (v: string | null | undefined) => (parse(v) ? dateFmt.format(parse(v)!) : "—");
export const fmtDateTime = (v: string | null | undefined) => (parse(v) ? dateTimeFmt.format(parse(v)!) : "—");
export const fmtDay = (v: string | null | undefined) => (parse(v) ? dayFmt.format(parse(v)!) : "—");
export const fmtTime = (v: string | null | undefined) => (parse(v) ? timeFmt.format(parse(v)!) : "—");

export function fmtNumber(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: digits }).format(n);
}

/** Words that keep their own capitals when a key is turned into a label. */
const PROPER: Record<string, string> = {
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  youtube: "YouTube",
  tiktok: "TikTok",
  instagram: "Instagram",
  facebook: "Facebook",
  uk: "UK",
  eu: "EU",
  nda: "NDA",
  api: "API",
  url: "URL",
  dns: "DNS",
  gdpr: "GDPR",
  pecr: "PECR",
  ai: "AI",
};

/** "meeting_booked" -> "Meeting booked"; "ideal_customer" -> "Ideal customer"; "linkedin" -> "LinkedIn". */
export function humanise(key: string): string {
  if (/^(us|usa)$/i.test(key.trim())) return key.trim().toUpperCase();
  const s = key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => PROPER[w.toLowerCase()] ?? w)
    .join(" ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A stage's label; stages created without one carry their key ("in_sequence"), which is shown as words. */
export function stageLabel(s: { key: string; label: string } | undefined | null, fallbackKey = ""): string {
  if (!s) return fallbackKey ? humanise(fallbackKey) : "";
  return !s.label || s.label === s.key ? humanise(s.key) : s.label;
}

/** Money in the business's currency, short for large sums: "£2.4m", "£850k", "£2,400". */
export function fmtMoney(n: number | null | undefined, currency = "GBP"): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const code = /^[A-Z]{3}$/.test(currency) ? currency : "GBP";
  const sym = new Intl.NumberFormat("en-GB", { style: "currency", currency: code, maximumFractionDigits: 0 }).formatToParts(0).find((p) => p.type === "currency")?.value ?? "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sym}${fmtNumber(n / 1_000_000, 1)}m`;
  if (abs >= 100_000) return `${sym}${fmtNumber(n / 1_000, 0)}k`;
  return `${sym}${fmtNumber(n)}`;
}

/** "today", "tomorrow", "yesterday", otherwise a date. For "next action" style dates. */
export function fmtDayRelative(v: string | null | undefined, now = new Date()): string {
  const d = parse(v);
  if (!d) return "—";
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((start(d) - start(now)) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return d.getFullYear() === now.getFullYear() ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(d) : dateFmt.format(d);
}

const SMALL = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
/** Spelled out up to twelve, as in running text. */
export function countWord(n: number): string {
  return n >= 0 && n < SMALL.length ? SMALL[n]! : fmtNumber(n);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

export function relativeDue(v: string | null | undefined, now = new Date()): string {
  const d = parse(v);
  if (!d) return "";
  const days = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  if (days < -1) return `overdue by ${-days} days`;
  if (days === -1) return "overdue since yesterday";
  if (days === 0) return "due today";
  if (days === 1) return "due tomorrow";
  return `due in ${days} days`;
}

/** The address if it is an http(s) web link, else null. Only these are rendered as links (no `javascript:`, `data:` ...). */
export function webUrl(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  return /^https?:\/\/\S+$/i.test(s) ? s : null;
}
